import React, { useEffect, useRef } from 'react';
import { DateTime, Duration } from 'luxon';
import styled from '@emotion/styled';
import { MultiplierStyleProps } from '.';
import _ from 'lodash';
import { useDates, useI18n } from '~Hooks';

const getMinWidth = (size: number | undefined) => {
  if (size === 3 || size === 2) return '50px';
  if (size === 1 || size === 0) return '60px';
  return '70px';
};

const TimeWrapper = styled.div<MultiplierStyleProps>`
  font-size: calc(20px - 0.1em * ${({ styleMultiplier }) => styleMultiplier?.size});
  color: #fff;
  min-width: ${({ styleMultiplier }) => getMinWidth(styleMultiplier?.size)};
  text-align: center;
  padding-top: ${({ styleMultiplier }) => `calc(16px - 0.1em * ${styleMultiplier?.space})`};
  padding-bottom: ${({ styleMultiplier }) => `calc(16px - 0.1em * ${styleMultiplier?.space})`};
  padding-right: 8px;
  padding-left: 8px;
`;

interface TimeProps {
  pickupTimeRaw?: any | string | null;
  onChangePickupTime: (newTime: string) => void;
  styleMultiplier: { size?: number; space?: number };
  createdAt: string;
}

/*
 * Component responsible for displaying a time counter on the right side of the Service Type.
 * Most common case is that it counts upwards from the createdAt time.

 * In case its a pre-order:
 * - If it has a pickup-time - it counts downwards towards the pickup-time.
 * - If its days ahead, display that day instead of the time.
 */
const Time: React.FunctionComponent<TimeProps> = ({
  pickupTimeRaw,
  onChangePickupTime,
  styleMultiplier,
  createdAt,
}) => {
  const { i18n } = useI18n();
  const dates = useDates();

  const endOfDay = DateTime.fromJSDate(dates.getEndDate(new Date()));

  const intervalRef = useRef<number | null>(null);
  const nowRef = useRef(DateTime.now());

  // Calculate time difference
  const getDiff = (): Duration => {
    nowRef.current = DateTime.now();
    if (pickupTimeRaw) {
      const pickupDateTime = DateTime.fromISO(pickupTimeRaw);
      return pickupDateTime.diff(nowRef.current, ['days', 'hours', 'minutes', 'seconds']);
    } else {
      const createdAtDateTime = DateTime.fromISO(createdAt);
      return nowRef.current.diff(createdAtDateTime, ['minutes', 'seconds']);
    }
  };

  // Format the time display for far-future dates
  const formatTime = (currentTime: Duration): string => {
    if (pickupTimeRaw) {
      const pickupDateTime = DateTime.fromISO(pickupTimeRaw);

      if (pickupDateTime > endOfDay) {
        if (pickupDateTime.hasSame(nowRef.current.plus({ days: 1 }), 'day')) {
          return i18n('General.Tomorrow');
        }
        return pickupDateTime.toFormat('cccc'); // Returns day of the week (e.g., "Monday")
      }

      if (!currentTime.isValid) {
        return i18n('KDS.Delayed');
      }

      const totalSeconds = currentTime.as('seconds');
      const isNegative = totalSeconds < 0;

      const absSeconds = Math.abs(totalSeconds);
      const days = Math.floor(absSeconds / (24 * 3600));
      const hours = Math.floor((absSeconds % (24 * 3600)) / 3600);
      const minutes = Math.floor((absSeconds % 3600) / 60);
      const seconds = Math.floor(absSeconds % 60);

      if (days >= 1) {
        return `${isNegative ? '-' : ''}${days}d`;
      }

      if (hours >= 1) {
        return `${isNegative ? '-' : ''}${hours}h ${_.padStart(minutes.toString(), 2, '0')}m`;
      }

      return `${isNegative ? '-' : ''}${_.padStart(minutes.toString(), 1, '0')}:${_.padStart(
        seconds.toString(),
        2,
        '0',
      )}`;
    }

    const totalMinutes = currentTime.as('minutes');
    if (totalMinutes < 60) {
      const minutes = Math.floor(Math.abs(totalMinutes));
      const seconds = Math.floor(Math.abs(currentTime.as('seconds')) % 60);
      return `${_.padStart(minutes.toString(), 2, '0')}:${_.padStart(seconds.toString(), 2, '0')}`;
    }

    return i18n('KDS.Delayed');
  };

  // Calculate border color dynamically
  const getBorderColor = (currentTime: Duration) => {
    const pickupDateTime = pickupTimeRaw ? DateTime.fromISO(pickupTimeRaw) : null;

    if (pickupDateTime && pickupDateTime > endOfDay) {
      return 'rgb(0 0 0 / 20%)';
    }

    if (!pickupTimeRaw) {
      return currentTime.as('minutes') < 5 ? 'rgb(0 0 0 / 20%)' : 'var(--colorUIRed)';
    }

    return currentTime.as('minutes') < 0 || !currentTime.isValid
      ? 'var(--colorUIRed)'
      : 'rgb(0 0 0 / 20%)';
  };

  useEffect(() => {
    // Set an interval to update nowRef, no need for React state
    intervalRef.current = window.setInterval(() => {
      nowRef.current = DateTime.now();
    }, 1000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [pickupTimeRaw, createdAt]);

  const currentTime = getDiff();
  const borderColor = getBorderColor(currentTime);

  return (
    <TimeWrapper
      styleMultiplier={styleMultiplier}
      style={{
        backgroundColor: borderColor,
      }}>
      {formatTime(currentTime)}
    </TimeWrapper>
  );
};

export default Time;
